import { Controller } from "@hotwired/stimulus";

// import mapboxgl from "../../../../assets/javascripts/vendor/mapbox-gl";
// import "../../../../assets/stylesheets/vendor/mapbox-gl.css";

import Rails from "@rails/ujs";
import _ from "underscore";

export default class extends Controller {
  static targets = ["map", "mapData"];
  static values = {
    boundsLat1: Number,
    boundsLng1: Number,
    boundsLat2: Number,
    boundsLng2: Number,
    lat: Number,
    lng: Number,
    listingsScope: Boolean,
  };

  connect() {
    this.userHasInteracted = false;
    this.mapData = JSON.parse(this.mapDataTarget.value).map_data;

    import("../../../../assets/javascripts/vendor/mapbox-gl")
      .then((module) => {
        import("../../../../assets/stylesheets/vendor/mapbox-gl.css").catch((error) => {
          console.error("Failed to load Mapbox CSS:", error);
        });
        this.mapboxgl = module.default;
        this.initMap(this.mapboxgl);

        if (!this.refreshUrl) return;

        this.debouncedOnMove = _.debounce(() => this.onMapMove(), 500);
        this.addMapListeners();
      })
      .catch((error) => {
        console.error("Failed to load Mapbox GL:", error);
      });
  }

  initMap(mapboxgl) {
    this.map = new mapboxgl.Map({
      accessToken: this.accessToken,
      container: this.mapTarget,
      center: [this.lngValue, this.latValue], // starting position [lng, lat]
      zoom: 12, // starting zoom
      scrollZoom: this.listingsScopeValue,
      cooperativeGestures: !this.listingsScopeValue,
    });

    this.map.scrollZoom.disable();
    this.map.addControl(new mapboxgl.NavigationControl());

    if (this.disableMapFit && this.countryBounds) {
      this.map.fitBounds(this.countryBounds, {
        padding: this.fitPadding,
      });
    } else {
      this.fitMap();
    }

    this.map.on("load", () => {
      this.map.addSource("locations", {
        type: "geojson",
        data: this.mapData,
        cluster: true,
        clusterMaxZoom: 11, // Max zoom to cluster points on
        clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
      });

      this.map.addLayer({
        id: "clusters",
        type: "circle",
        source: "locations",
        filter: ["has", "point_count"],
        paint: {
          "circle-radius": 0,
        },
      });

      this.markers = {};
      this.markersOnScreen = {};

      this.map.on("idle", () => {
        if (this.userHasInteracted) return;
        if (!this.map.isSourceLoaded("locations")) return;
        this.updateMarkers();
      });
    });
  }

  addMapListeners() {
    const markUser = (e) => {
      if (e.originalEvent) {
        this.userHasInteracted = true;
      }
    };
    this.map.on("dragstart", markUser);
    this.map.on("zoomstart", markUser);
    this.map.on("rotatestart", markUser);
    this.map.on("pitchstart", markUser);
    this.map.on("wheel", markUser);
    this.map.on("touchstart", markUser);

    this.map.on("move", this.debouncedOnMove);
  }

  onMapMove() {
    if (!this.userHasInteracted) return;

    const bounds = this.map.getBounds();
    const center = this.map.getCenter();
    Rails.ajax({
      url: this.refreshUrl,
      type: "GET",
      data: new URLSearchParams({
        map_changed: true,
        page: 1,
        "search[bounds_lat1]": bounds.getSouthWest().lat,
        "search[bounds_lng1]": bounds.getSouthWest().lng,
        "search[bounds_lat2]": bounds.getNorthEast().lat,
        "search[bounds_lng2]": bounds.getNorthEast().lng,
        "search[latitude]": center.lat,
        "search[longitude]": center.lng,
      }).toString(),
      dataType: "script",
    });
  }

  onListingsAdded(e) {
    this.userHasInteracted = e.detail?.mapChanged;

    const newMapData = JSON.parse(this.mapDataTarget.value).map_data;
    this.mapData = this.userHasInteracted
      ? newMapData
      : { ...this.mapData, features: [...this.mapData.features, ...newMapData.features] };

    if (this.userHasInteracted) {
      for (const id in this.markers) {
        this.markers[id].remove();
      }
      this.markersOnScreen = {};
      this.markers = {};
    }

    this.map.getSource("locations").setData(this.mapData);

    // setData() posts the new GeoJSON to a worker which rebuilds the internal tile index (geojson-vt) asynchronously;
    // if one calls querySourceFeatures() (in updateMarkers) immediately after setData(), it can still result in the old tiles/features being used.
    // For now, we add a small timeout here to ensure the data is ready
    // ("sourcedata" event doesn't help as it does not fire at large zoom out levels for some reason).
    setTimeout(() => {
      this.updateMarkers();
      this.fitMap();
    }, 250);
  }

  updateMarkers() {
    const newMarkers = {};
    const exactLocationMatch = new Map();
    const offset = 0.00005; // small offset value for shifting markers

    const features = this.map.querySourceFeatures("locations");

    for (const feature of features) {
      let [lng, lat] = feature.geometry.coordinates;
      let key = `${lat},${lng}`;

      // if there are listings with the exact same location, we shift the marker up.
      if (exactLocationMatch.has(key)) {
        let count = exactLocationMatch.get(key);
        lat += offset * count;
        lng += offset * count;
        exactLocationMatch.set(key, count + 1);
      } else {
        exactLocationMatch.set(key, 1);
      }

      const coords = [lng, lat];
      const props = feature.properties;

      const id = props.cluster ? "cluster_" + props.cluster_id : props.id;
      let marker = this.markers[id];

      if (!marker) {
        const el = props.cluster ? this.createClusteredMarker(props, coords) : this.createPriceMarker(props);
        marker = this.markers[id] = new this.mapboxgl.Marker({
          element: el,
        }).setLngLat(coords);

        if (!props.cluster) {
          const infoBlock = `<a href="${props.url}" style="outline: none !important;">
                    <div class="info-window">
                      <div class="image-box">
                        <img src=${props.image}>
                      </div>
                      <div class="content">
                        <h4 class="title">${props.title}</h4>
                        <span>${props.buildingType}</span>
                      </div>
                      <p class="price">${props.details}</p>
                    </div>
                  </a>`;

          const popup = new this.mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(infoBlock);
          marker.setPopup(popup);
        }
      }

      newMarkers[id] = marker;
      if (!this.markersOnScreen[id]) marker.addTo(this.map);
    }
    // for every marker we've added previously, remove those that are no longer visible
    for (const id in this.markersOnScreen) {
      if (!newMarkers[id]) this.markersOnScreen[id].remove();
    }
    this.markersOnScreen = newMarkers;
  }

  createClusteredMarker(props, coords) {
    const el = document.createElement("div");
    el.className = "mapCluster";
    el.style.width = "36px";
    el.style.height = "36px";
    el.style.color = "#fff";
    el.style.textAlign = "center";
    el.style.fontSize = "16px";
    el.style.lineHeight = "32px";
    el.style.fontWeight = "800";
    el.style.display = "flex";
    el.style.justifyContent = "center";
    el.style.alignContent = "center";
    el.style.background = "var(--primary-color)";
    el.style.borderRadius = "50%";
    el.style.border = "2px solid #fff";
    el.innerText = props.point_count;

    const that = this;
    const clusterId = props.cluster_id;

    el.addEventListener("click", (event) => {
      that.map.getSource("locations").getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        that.map.easeTo({
          center: coords,
          zoom: zoom,
        });
      });
    });

    return el;
  }

  createPriceMarker(props) {
    const el = document.createElement("div");
    el.style.border = "1px solid var(--primary-color)";
    el.style.backgroundColor = "#fff";
    el.style.padding = "10px 16px";
    el.style.borderRadius = "4px";
    el.style.cursor = "pointer";
    el.style.opacity = 1;
    el.appendChild(document.createTextNode(props.price));

    if (!this.listingsScopeValue) {
      let htmlString = `
        <p class="font-semibold">${props.property_name}</p>
        <p>${props.property_info}</p>
      `;
      el.innerHTML = htmlString.trim();
    }

    return el;
  }

  fitMap() {
    if (!this.mapData.features[0] || this.userHasInteracted) {
      return;
    }

    const bounds = new this.mapboxgl.LngLatBounds();
    this.mapData.features.forEach((feature) => {
      bounds.extend(feature.geometry.coordinates);
    });

    if (this.map?.loaded()) {
      this.map.fitBounds(bounds, {
        padding: this.fitPadding,
      });
    } else {
      this.map.once("load", () => {
        this.map.fitBounds(bounds, {
          padding: this.fitPadding,
        });
      });
    }
  }

  get fitPadding() {
    if (this.listingsScopeValue) {
      return 30;
    }

    // mobile
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      return 70;
    }

    // desktop
    return 120;
  }

  get accessToken() {
    return this.data.get("accessToken");
  }

  get refreshUrl() {
    return this.data.get("refreshUrl");
  }

  get disableMapFit() {
    return this.data.get("disableMapFit") === "true";
  }

  get countryBounds() {
    if (!this.data.get("swLat") || !this.data.get("swLon") || !this.data.get("neLat") || !this.data.get("neLon")) {
      return null;
    }

    const sw = new this.mapboxgl.LngLat(parseFloat(this.data.get("swLon")), parseFloat(this.data.get("swLat")));
    const ne = new this.mapboxgl.LngLat(parseFloat(this.data.get("neLon")), parseFloat(this.data.get("neLat")));
    return new this.mapboxgl.LngLatBounds(sw, ne);
  }
}
